import { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, getTokens } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";

let contextCache = { token: null, promise: null };
let partnersCache = { token: null, promise: null };

function cached(cache, setCache, path) {
  const token = getTokens()?.accessToken || null;
  if (!cache.promise || cache.token !== token) {
    const promise = apiGet(path).catch((err) => {
      setCache({ token: null, promise: null });
      throw err;
    });
    setCache({ token, promise });
    return promise;
  }
  return cache.promise;
}

export function loadPortalContext() {
  return cached(contextCache, (next) => (contextCache = next), "/portal/context");
}

export function loadPartners() {
  return cached(partnersCache, (next) => (partnersCache = next), "/portal/partners");
}

export default function usePortalAudience() {
  const auth = useAuth();
  const { user, hasPermission } = auth;
  const authPortal = auth.portal || null;
  const [fetched, setFetched] = useState(null);
  const [loading, setLoading] = useState(!authPortal);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authPortal) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    loadPortalContext()
      .then((res) => {
        if (!cancelled) setFetched(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load your portal context.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [authPortal]);

  const context = authPortal || fetched;
  const isTenant = context?.kind === "TENANT";
  const isPartner = context?.kind === "PARTNER";
  const canManage = Boolean(context?.canManage);
  const contextTenantId = context?.tenant?.id || null;
  const can = useCallback(
    (permission) => isTenant && canManage && hasPermission(permission, contextTenantId),
    [isTenant, canManage, hasPermission, contextTenantId],
  );

  return useMemo(
    () => ({
      loading,
      error,
      context,
      isTenant,
      isPartner,
      canManage,
      tenantId: context?.tenant?.id || null,
      tenantName: context?.tenant?.name || null,
      partner: context?.partner || null,
      userId: user?.id || null,
      can,
    }),
    [loading, error, context, isTenant, isPartner, canManage, user, can],
  );
}

export function usePartnerNames(enabled) {
  const [names, setNames] = useState({});
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    loadPartners()
      .then((res) => {
        if (cancelled) return;
        const map = {};
        for (const p of res.partners || []) map[p.id] = p.name;
        setNames(map);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [enabled]);
  return names;
}

export function createdByLabel(market, audience, partnerNames) {
  const platform =
    market.createdByPlatform ?? (market.createdBy === "SYSTEM_AUTOMATION" || (market.sourceType && market.sourceType !== "MANUAL"));
  const self = audience.userId && market.createdBy === audience.userId;
  const person = self ? "You" : market.createdByName || null;
  if (audience.isPartner) {
    if (market.partnerId || !platform) return { label: person || "Your organization", tone: "green" };
    return { label: "Platform", tone: "gray" };
  }
  if (market.partnerId) {
    const partner = partnerNames[market.partnerId] || "unnamed partner";
    return { label: person ? `${person} · ${partner}` : `Partner: ${partner}`, tone: "blue" };
  }
  if (platform) return { label: "Platform", tone: "gray" };
  return { label: person || "Your team", tone: "green" };
}
